// Same value as seaticsPrecheckoutWebViewFix's NATIVE_WEBVIEW_HTML_ATTR; not imported so
// this module stays tiny in the main bundle.
const NATIVE_WEBVIEW_HTML_ATTR = 'data-promotix-native-webview';

/**
 * Real-user Core Web Vitals -> GA4, measured the same way Chrome measures them for
 * Search Console (CrUX). One event per metric per page view: LCP, CLS, INP.
 *
 * Search Console passes a URL group when p75 is "good", i.e. >= 75% of page views are
 * rated good. So in GA4 the share of events with metric_rating = "good" is the number
 * to watch (filter Chrome + mobile to match CrUX). See HOW_TO_DEPLOY.md.
 */

export const pageTypeForPath = pathname => {
  const p = String(pathname || '');
  if (/^\/event-discovery\/l\//.test(p) || /^\/l\//.test(p)) return 'listing';
  if (p === '/' || /^\/s(\/|$)/.test(p) || /^\/event-discovery\/?$/.test(p)) return 'search';
  if (/^\/event-discovery\//.test(p)) return 'discovery';
  if (/\/checkout(\/|$)/.test(p)) return 'checkout';
  return 'other';
};

/** GA4 event values must be integers; CLS is reported x1000 (0.13 -> 130). */
const ga4Value = metric =>
  Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta);

const debugTargetFor = metric => {
  const a = metric.attribution || {};
  if (metric.name === 'LCP') return a.target || a.url || '';
  if (metric.name === 'CLS') return a.largestShiftTarget || '';
  if (metric.name === 'INP') return a.interactionTarget || '';
  return '';
};

export const ga4ParamsForMetric = (metric, win = typeof window !== 'undefined' ? window : null) => {
  const pathname = win?.location?.pathname || '';
  const params = {
    value: ga4Value(metric),
    metric_id: metric.id,
    metric_value: metric.name === 'CLS' ? Number(metric.value.toFixed(4)) : Math.round(metric.value),
    metric_delta: metric.name === 'CLS' ? Number(metric.delta.toFixed(4)) : Math.round(metric.delta),
    metric_rating: metric.rating,
    page_type: pageTypeForPath(pathname),
    app_webview:
      win?.document?.documentElement?.getAttribute?.(NATIVE_WEBVIEW_HTML_ATTR) === '1' ? 'yes' : 'no',
    debug_target: String(debugTargetFor(metric)).slice(0, 100),
    non_interaction: true,
  };
  if (metric.name === 'LCP' && metric.attribution) {
    params.lcp_ttfb = Math.round(metric.attribution.timeToFirstByte || 0);
    params.lcp_load_delay = Math.round(metric.attribution.resourceLoadDelay || 0);
    params.lcp_load_duration = Math.round(metric.attribution.resourceLoadDuration || 0);
    params.lcp_render_delay = Math.round(metric.attribution.elementRenderDelay || 0);
  }
  return params;
};

const reportMetric = metric => {
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', metric.name, ga4ParamsForMetric(metric));
  } catch (e) {
    // Reporting is best-effort; never surface errors to shoppers.
  }
};

let installed = false;

/**
 * Loads web-vitals on the first idle moment after render (not at window load, which
 * would drop visitors who leave slow pages early — CrUX still counts those).
 * The library reads buffered performance entries, so late registration still
 * captures LCP/CLS from page start.
 */
export const installWebVitalsReporting = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const start = () => {
    import(/* webpackChunkName: "webVitals" */ 'web-vitals/attribution')
      .then(({ onLCP, onCLS, onINP }) => {
        onLCP(reportMetric);
        onCLS(reportMetric);
        onINP(reportMetric);
      })
      .catch(() => {
        // Reporting is best-effort; never surface errors to shoppers.
      });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: 3000 });
  } else {
    setTimeout(start, 1000);
  }
};
